import { useEffect, useState } from 'react';
import logoNoBg from '../assets/logo-no-bg.png';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthContext } from '../utils/Context';
import TextInput from '../components/TextInput';
import Alert from '../components/Alert';
import { Helmet } from 'react-helmet-async';
import Button from '../components/Button';
import Spinner from '../components/Spinner';
import { classNames } from '../domain/helpers';
import TurnstileComponent from '../components/TurnstileComponent';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const [errors, setErrors] = useState<AlertType>(null);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [context, setContext] = useAuthContext((state) => [state.auth, state.changeState]);
    const [requestPending, setRequestPending] = useState<boolean>(false);
    const [turnstileToken, setTurnstileToken] = useState<string>('');
    useEffect(() => {
        if (context.isLoggedIn) {
            navigate('/dashboard');
        }
    }, []);

    const togglePasswordVisibility = (e: any) => {
        e.preventDefault();
        setShowPassword((prevState) => !prevState);
    };

    const handleSubmit = (event: any) => {
        event.preventDefault();

        if (turnstileToken === '' || turnstileToken === undefined || turnstileToken === null) {
            return setErrors({
                message: 'Please complete the captcha',
                alertType: 'error',
            });
        }

        setRequestPending(true);
        const data: LoginForm = {
            email: event.target.email.value,
            password: event.target.password.value,
        };

        axios
            .post('/auth/login', {
                ...data,
                turnstileToken: turnstileToken,
            })
            .then((response) => {
                setErrors(null);
                // toast.success("Successfully Logged in. \nRedirecting to dashboard...");
                setErrors({
                    message: 'Successfully logged in. \nRedirecting...',
                    alertType: 'success',
                });
                setRequestPending(false);
                setTimeout(() => {
                    setContext({
                        firstName: response.data.firstName as string,
                        lastName: response.data.lastName as string,
                        email: response.data.email as string,
                        token: response.data.token as string,
                        plan: response.data.plan as number,
                        isLoggedIn: true,
                        orgName: response.data.organisationName,
                        orgRole: response.data.orgRole,
                    });
                    if (location.search !== '') {
                        return navigate(location.search.split('=')[1]);
                    } else {
                        return navigate('/dashboard');
                    }
                }, 2000);
            })
            .catch((error) => {
                setErrors({ ...error.response.data, alertType: 'error' });
                setRequestPending(false);
                //@ts-ignore
                turnstile.reset();
            });
    };

    return (
        <>
            <Helmet>
                <title>Login | Webbsly</title>
                <link rel="canonical" href={window.location.href} />
                <meta name="description" content="Login | Webbsly" />
            </Helmet>
            <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-sm">
                    <img className="mx-auto h-20 w-auto" src={logoNoBg} alt="Webbsly" />
                    <h1 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
                        Sign in to your account
                    </h1>
                </div>

                <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
                                Email address
                            </label>
                            <div className="mt-2">
                                <TextInput id="email" name="email" type="email" autocomplete="email" required={true} />
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between">
                                <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900">
                                    Password
                                </label>
                                <div className="text-sm">
                                    <Link
                                        tabIndex={-1}
                                        to="/recover"
                                        onClick={(e) => requestPending && e.preventDefault()}
                                        className={classNames(
                                            'font-semibold text-indigo-600 hover:text-indigo-500',
                                            requestPending ? 'opacity-50 cursor-not-allowed' : '',
                                        )}>
                                        Forgot password?
                                    </Link>
                                </div>
                            </div>
                            <div className="mt-2 relative z-0">
                                <TextInput
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    autocomplete="password"
                                    required={true}
                                    decorationRight={
                                        showPassword ? (
                                            <EyeSlashIcon className="w-6 h-6 stroke-gray-600" />
                                        ) : (
                                            <EyeIcon className="w-6 h-6 stroke-gray-600" />
                                        )
                                    }
                                    decorationRightOnClick={togglePasswordVisibility}
                                />
                            </div>
                        </div>
                        <TurnstileComponent callback={setTurnstileToken} />
                        {/* <div id="turnstile-custom"></div> */}

                        {requestPending && <Spinner className="w-full text-center" />}

                        {errors && <Alert alertType={errors.alertType} message={errors.message} />}

                        <div>
                            <Button type="submit" disabled={requestPending}>
                                Sign in
                            </Button>
                        </div>
                    </form>

                    <p className="mt-10 text-center text-sm text-gray-500">
                        Not a member?{' '}
                        <Link
                            to="/pricing"
                            onClick={(e) => requestPending && e.preventDefault()}
                            className={classNames(
                                'font-semibold leading-6 text-indigo-600 hover:text-indigo-500',
                                requestPending ? 'opacity-50 cursor-not-allowed' : '',
                            )}>
                            Start a 14 day free trial
                        </Link>
                    </p>
                </div>
            </div>
        </>
    );
}

export default Login;
